(function () {
  'use strict';

  class PawtiqueUpsellPopup {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('.paw-upsell__dialog');
      this.items = Array.from(root.querySelectorAll('[data-paw-upsell-item]'));
      this.timerMinutesEl = root.querySelector('[data-paw-upsell-timer-minutes]');
      this.timerSecondsEl = root.querySelector('[data-paw-upsell-timer-seconds]');
      this.globalError = root.querySelector('[data-paw-upsell-global-error]');
      this.continueButton = root.querySelector('[data-paw-upsell-continue]');
      this.selectedCountEl = root.querySelector('[data-paw-upsell-selected-count]');
      this.tierStatusEl = root.querySelector('[data-paw-upsell-progress-message]');

      this.countdownMinutes = Math.max(1, Number(root.dataset.countdownMinutes || 5));
      this.maxQuantity = Math.max(1, Number(root.dataset.maxQuantity || 5));
      this.currencyCode = root.dataset.currencyCode || 'USD';
      this.shopCurrency = root.dataset.shopCurrency || this.currencyCode;
      this.tierStatusEnabled = root.dataset.tierStatusEnabled === 'true';
      this.showSelectedCount = root.dataset.showSelectedCount === 'true';
      this.continueBehavior = root.dataset.continueBehavior || 'checkout';
      this.mainInitialPrice = Number(root.dataset.mainInitialPrice || 0);
      this.mainInitialCompare = Number(root.dataset.mainInitialCompare || 0);

      this.tiers = this.buildTiers();
      this.pendingByVariant = new Map();
      this.cartQtyByVariant = new Map();
      this.cartItemsSubtotalCents = 0;
      this.cartTotalCents = 0;
      this.cartItemCount = 0;
      this.previousFocus = null;
      this.timerId = null;
      this.isOpen = false;
      this.finishInProgress = false;
      this.lastPrimaryVariantId = '';
      this.lastPrimaryHandledAt = 0;
      this.currentCartTierDiscount = 0;
      this.otherCartFactor = 1;

      this.prepareItems();
      this.bindEvents();
    }

    buildTiers() {
      const raw = [
        {
          index: 1,
          enabled: this.root.dataset.tier1Enabled === 'true',
          threshold: Number(this.root.dataset.tier1Threshold || 35),
          discount: 0,
          kind: 'shipping',
          title: this.root.dataset.tier1Title || 'Free shipping'
        },
        {
          index: 2,
          enabled: this.root.dataset.tier2Enabled === 'true',
          threshold: Number(this.root.dataset.tier2Threshold || 60),
          discount: Number(this.root.dataset.tier2Discount || 5),
          kind: 'order',
          title: this.root.dataset.tier2Title || 'Extra 5% off'
        },
        {
          index: 3,
          enabled: this.root.dataset.tier3Enabled === 'true',
          threshold: Number(this.root.dataset.tier3Threshold || 90),
          discount: Number(this.root.dataset.tier3Discount || 10),
          kind: 'order',
          title: this.root.dataset.tier3Title || 'Extra 10% off'
        },
        {
          index: 4,
          enabled: this.root.dataset.tier4Enabled === 'true',
          threshold: Number(this.root.dataset.tier4Threshold || 130),
          discount: Number(this.root.dataset.tier4Discount || 15),
          kind: 'order',
          title: this.root.dataset.tier4Title || 'Extra 15% off'
        }
      ];

      return raw
        .filter((tier) => tier.enabled && Number.isFinite(tier.threshold) && tier.threshold > 0)
        .sort((a, b) => a.threshold - b.threshold)
        .map((tier) => ({ ...tier, thresholdCents: 0 }));
    }

    prepareItems() {
      this.items.forEach((item) => {
        const json = item.querySelector('[data-paw-upsell-variants]');
        try {
          item._pawVariants = json ? JSON.parse(json.textContent || '[]') : [];
        } catch (error) {
          item._pawVariants = [];
          console.warn('[Pawtique Upsell] Invalid variant JSON', error);
        }

        const hidden = item.querySelector('[data-paw-upsell-variant]');
        const currentId = hidden ? String(hidden.value || '') : '';
        item._pawVariant = item._pawVariants.find((variant) => String(variant.id) === currentId)
          || item._pawVariants.find((variant) => variant.available)
          || item._pawVariants[0]
          || null;
        item._pawDraftQty = 1;

        this.applyVariant(item, item._pawVariant);
        this.updateOptionAvailability(item);
      });
    }

    bindEvents() {
      document.addEventListener('submit', (event) => {
        if (!this.isProductPage() || !this.isProductAddForm(event.target)) return;
        this.armDrawerSuppression();
      }, true);

      this.root.querySelectorAll('[data-paw-upsell-dismiss]').forEach((el) => {
        el.addEventListener('click', () => this.dismiss());
      });

      if (this.continueButton) {
        this.continueButton.addEventListener('click', () => this.finish());
      }

      this.items.forEach((item) => {
        item.querySelectorAll('[data-paw-upsell-option-select]').forEach((select) => {
          select.addEventListener('change', () => this.selectOption(item));
        });

        const minus = item.querySelector('[data-paw-upsell-qty-minus]');
        const plus = item.querySelector('[data-paw-upsell-qty-plus]');
        const add = item.querySelector('[data-paw-upsell-add]');
        const remove = item.querySelector('[data-paw-upsell-remove]');

        if (minus) minus.addEventListener('click', () => this.changeQuantity(item, -1));
        if (plus) plus.addEventListener('click', () => this.changeQuantity(item, 1));
        if (add) add.addEventListener('click', () => this.selectUpsell(item));
        if (remove) remove.addEventListener('click', () => this.removeCurrentVariant(item));
      });

      document.addEventListener('ajaxProduct:added', (event) => {
        if (!this.isProductPage()) return;
        this.armDrawerSuppression();
        this.suppressCartDrawers();
        if (event.detail && event.detail.product) this.handlePrimaryAdded(event.detail.product);
      }, true);

      document.addEventListener('product-form:submit:success', (event) => {
        if (!this.isProductPage() || !event.detail || !event.detail.response) return;
        this.armDrawerSuppression();
        this.handlePrimaryAdded(event.detail.response, event.detail.cart);
      }, true);

      document.addEventListener('product-form:submit:error', () => {
        if (!this.isOpen) this.disarmDrawerSuppression();
      }, true);

      document.addEventListener('product-form:cart:navigate', (event) => {
        if (!this.isProductPage()) return;
        event.preventDefault();
      }, true);

      document.addEventListener('keydown', (event) => {
        if (!this.isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.dismiss();
        } else if (event.key === 'Tab') {
          this.trapFocus(event);
        }
      });
    }

    isProductPage() {
      return document.body.classList.contains('template-product');
    }

    isProductAddForm(form) {
      if (!form || form.tagName !== 'FORM') return false;
      const action = String(form.getAttribute('action') || '');
      if (action.indexOf('/cart/add') !== -1) return true;
      return !!form.querySelector('[name="id"]') && !!form.querySelector('button[type="submit"],input[type="submit"]');
    }

    armDrawerSuppression() {
      document.documentElement.classList.add('paw-upsell-awaiting');
    }

    disarmDrawerSuppression() {
      document.documentElement.classList.remove('paw-upsell-awaiting');
    }

    handlePrimaryAdded(product, cart) {
      const primaryVariantId = String(product && (product.variant_id || product.id) || '');
      const now = Date.now();

      if (primaryVariantId && primaryVariantId === this.lastPrimaryVariantId && now - this.lastPrimaryHandledAt < 1200) {
        if (cart && Array.isArray(cart.items)) this.applyCartState(cart);
        return;
      }

      this.lastPrimaryVariantId = primaryVariantId;
      this.lastPrimaryHandledAt = now;
      this.pendingByVariant.clear();
      this.items.forEach((item) => {
        item._pawDraftQty = 1;
        item.hidden = false;
        item.removeAttribute('aria-hidden');
        item.style.removeProperty('display');
        this.clearItemStatus(item);
      });

      this.updateCurrentProduct(product);
      this.open();
      this.suppressCartDrawers();

      if (cart && Array.isArray(cart.items)) this.applyCartState(cart);
      else this.fetchCartState();
    }

    updateCurrentProduct(product) {
      const titleEl = this.root.querySelector('[data-paw-upsell-current-title]');
      const variantEl = this.root.querySelector('[data-paw-upsell-current-variant]');
      const imageEl = this.root.querySelector('.paw-upsell__current-image');
      const price = Number(product && (product.final_price || product.price) || this.mainInitialPrice || 0);
      const compare = Number(product && (product.original_price || product.compare_at_price) || this.mainInitialCompare || 0);

      if (titleEl) titleEl.textContent = this.root.dataset.mainShortTitle || product.product_title || product.title || '';
      if (variantEl) variantEl.textContent = this.cleanVariantTitle(product.variant_title || product.title || '');

      if (imageEl) {
        let image = product.image;
        if (!image && product.featured_image) {
          image = typeof product.featured_image === 'string'
            ? product.featured_image
            : product.featured_image.url || product.featured_image.src;
        }
        if (image) this.setImageSource(imageEl, image);
      }

      this.updateCurrentPrice(price, compare);
    }

    updateCurrentFromCart(cart) {
      if (!this.lastPrimaryVariantId || !cart || !Array.isArray(cart.items)) return;
      const line = cart.items.find((item) => String(item.variant_id || item.id) === String(this.lastPrimaryVariantId));
      if (!line) return;

      const titleEl = this.root.querySelector('[data-paw-upsell-current-title]');
      const variantEl = this.root.querySelector('[data-paw-upsell-current-variant]');
      const imageEl = this.root.querySelector('.paw-upsell__current-image');
      const price = Number(line.final_price || line.price || this.mainInitialPrice || 0);
      const originalFromCart = Number(line.original_price || line.price || 0);
      const compare = Math.max(originalFromCart, Number(this.mainInitialCompare || 0));

      if (titleEl) titleEl.textContent = this.root.dataset.mainShortTitle || line.product_title || line.title || titleEl.textContent;
      if (variantEl) variantEl.textContent = this.cleanVariantTitle(line.variant_title || '');
      if (imageEl && line.image) this.setImageSource(imageEl, line.image);
      this.updateCurrentPrice(price, compare);
    }

    updateCurrentPrice(price, compare) {
      const priceEl = this.root.querySelector('[data-paw-upsell-current-price]');
      const compareEl = this.root.querySelector('[data-paw-upsell-current-compare]');
      const saveEl = this.root.querySelector('[data-paw-upsell-current-save]');
      const current = Number(price || 0);
      const original = Number(compare || 0);
      const saved = original > current ? original - current : 0;

      this.setMoney(priceEl, current);
      if (compareEl) {
        if (original > current) {
          this.setMoney(compareEl, original);
          compareEl.classList.remove('is-hidden');
        } else {
          compareEl.classList.add('is-hidden');
        }
      }
      if (saveEl) {
        if (saved > 0) {
          saveEl.textContent = (saveEl.dataset.label || 'You saved') + ' ' + this.formatMoney(saved);
          saveEl.classList.remove('is-hidden');
        } else {
          saveEl.classList.add('is-hidden');
        }
      }
    }

    selectOption(item) {
      if (this.finishInProgress) return;
      const selected = this.getSelectedOptions(item);
      const variant = (item._pawVariants || []).find((candidate) => {
        return candidate.options && candidate.options.every((option, optionIndex) => String(option) === String(selected[optionIndex] || ''));
      });

      if (variant) this.applyVariant(item, variant);
      this.updateOptionAvailability(item);
    }

    getSelectedOptions(item) {
      const selected = [];
      item.querySelectorAll('[data-paw-upsell-option-select]').forEach((select) => {
        selected[Number(select.dataset.optionIndex || 0)] = select.value || '';
      });
      return selected;
    }

    applyVariant(item, variant) {
      if (!variant) return;
      item._pawVariant = variant;

      const hidden = item.querySelector('[data-paw-upsell-variant]');
      if (hidden) {
        hidden.value = String(variant.id);
        hidden.dataset.available = String(!!variant.available);
      }

      const pending = this.pendingByVariant.get(String(variant.id));
      item._pawDraftQty = pending ? Number(pending.quantity || 1) : 1;

      const image = item.querySelector('[data-paw-upsell-image]');
      if (image && variant.image) {
        this.setImageSource(image, variant.image);
        if (variant.alt) image.alt = variant.alt;
      }

      this.updateItemPrice(item, variant);
      this.syncItem(item);
    }

    updateOptionAvailability(item) {
      const variants = item._pawVariants || [];
      const current = this.getSelectedOptions(item);

      item.querySelectorAll('[data-paw-upsell-option-select]').forEach((select) => {
        const index = Number(select.dataset.optionIndex || 0);
        Array.from(select.options).forEach((optionEl) => {
          const candidate = current.slice();
          candidate[index] = String(optionEl.value || '');
          const possible = variants.some((variant) => {
            if (!variant.available) return false;
            return (variant.options || []).every((option, optionIndex) => {
              const expected = candidate[optionIndex];
              return !expected || String(option) === String(expected);
            });
          });
          optionEl.disabled = !possible && optionEl.value !== select.value;
        });
      });
    }

    updateItemPrice(item, variant) {
      const price = Number(variant.price || 0);
      const compareAt = Number(variant.compare_at_price || 0);
      const percent = compareAt > price && compareAt > 0 ? Math.round((compareAt - price) / compareAt * 100) : 0;

      this.setMoney(item.querySelector('[data-paw-upsell-price]'), price);
      const compareEl = item.querySelector('[data-paw-upsell-compare]');
      if (compareEl) {
        if (compareAt > price) {
          this.setMoney(compareEl, compareAt);
          compareEl.classList.remove('is-hidden');
        } else {
          compareEl.classList.add('is-hidden');
        }
      }

      const saveEl = item.querySelector('[data-paw-upsell-save-percent]');
      if (saveEl) {
        if (percent > 0) {
          saveEl.textContent = (saveEl.dataset.label || 'Save') + ' ' + percent + '%';
          saveEl.classList.remove('is-hidden');
        } else {
          saveEl.classList.add('is-hidden');
        }
      }
    }

    changeQuantity(item, delta) {
      if (this.finishInProgress || !item._pawVariant || !item._pawVariant.available) return;
      const variantId = String(item._pawVariant.id);
      const pending = this.pendingByVariant.get(variantId);
      const current = pending ? Number(pending.quantity || 1) : Number(item._pawDraftQty || 1);
      let next = current + delta;

      if (pending && next <= 0) {
        this.pendingByVariant.delete(variantId);
        item._pawDraftQty = 1;
      } else {
        next = Math.max(1, Math.min(this.maxQuantity, next));
        item._pawDraftQty = next;
        if (pending) {
          pending.quantity = next;
          this.pendingByVariant.set(variantId, pending);
        }
      }

      this.clearItemStatus(item);
      this.syncAll();
    }

    selectUpsell(item) {
      const variant = item._pawVariant;
      if (!variant || !variant.available || this.finishInProgress) return;
      const variantId = String(variant.id);
      const qty = Math.max(1, Math.min(this.maxQuantity, Number(item._pawDraftQty || 1)));
      const existing = this.pendingByVariant.get(variantId);

      this.pendingByVariant.set(variantId, {
        variantId,
        productId: String(item.dataset.productId || ''),
        quantity: existing ? Math.min(this.maxQuantity, Number(existing.quantity || 0) + qty) : qty,
        priceCents: Number(variant.price || 0),
        title: item.dataset.productTitle || '',
        variantTitle: this.cleanVariantTitle(variant.title),
        options: Array.isArray(variant.options) ? variant.options.slice() : []
      });

      item._pawDraftQty = this.pendingByVariant.get(variantId).quantity;
      this.setItemStatus(item, 'success', 'Added to your order');
      this.syncAll();

      // Once an upsell product has been selected, remove its card from the
      // visible recommendation list while keeping the selection in memory.
      // The pending item is still submitted in the single batch request when
      // the customer continues to cart / checkout.
      item.hidden = true;
      item.setAttribute('aria-hidden', 'true');
      // Some theme CSS forces the product card to display with !important,
      // which can override the browser's native [hidden] rule. Force-hide the
      // whole recommendation card so it disappears immediately after Add.
      item.style.setProperty('display', 'none', 'important');
    }

    removeCurrentVariant(item) {
      const variant = item._pawVariant;
      if (!variant || this.finishInProgress) return;
      this.pendingByVariant.delete(String(variant.id));
      item._pawDraftQty = 1;
      this.clearItemStatus(item);
      this.syncAll();
    }

    syncAll() {
      this.items.forEach((item) => this.syncItem(item));
      this.updateProjection();
    }

    syncItem(item) {
      const variant = item._pawVariant;
      const add = item.querySelector('[data-paw-upsell-add]');
      const label = item.querySelector('[data-paw-upsell-add-label]');
      const remove = item.querySelector('[data-paw-upsell-remove]');
      const qtyEl = item.querySelector('[data-paw-upsell-qty]');
      const minus = item.querySelector('[data-paw-upsell-qty-minus]');
      const plus = item.querySelector('[data-paw-upsell-qty-plus]');
      const cartNote = item.querySelector('[data-paw-upsell-cart-note]');
      if (!variant || !add || !label) return;

      const variantId = String(variant.id);
      const pending = this.pendingByVariant.get(variantId);
      const currentQty = pending ? Number(pending.quantity || 1) : Number(item._pawDraftQty || 1);
      const inCartQty = Number(this.cartQtyByVariant.get(variantId) || 0);
      if (qtyEl) qtyEl.textContent = String(currentQty);

      if (minus) minus.disabled = this.finishInProgress || (!pending && currentQty <= 1);
      if (plus) plus.disabled = this.finishInProgress || currentQty >= this.maxQuantity;
      if (remove) remove.hidden = !pending;

      if (cartNote) {
        if (inCartQty > 0) {
          cartNote.hidden = false;
          cartNote.textContent = 'In cart: ' + inCartQty;
        } else {
          cartNote.hidden = true;
          cartNote.textContent = '';
        }
      }

      add.classList.toggle('is-added', !!pending);

      if (!variant.available) {
        add.disabled = true;
        label.textContent = add.dataset.soldOutText || 'Sold out';
      } else if (pending) {
        add.disabled = true;
        label.textContent = (add.dataset.addedText || 'Added') + (pending.quantity > 1 ? ' ×' + pending.quantity : '');
      } else {
        add.disabled = this.finishInProgress;
        label.textContent = add.dataset.addText || 'Add';
      }

      this.updateSelectedSummary(item);
    }

    updateSelectedSummary(item) {
      const summary = item.querySelector('[data-paw-upsell-selected-summary]');
      if (!summary) return;
      const productId = String(item.dataset.productId || '');
      const selected = Array.from(this.pendingByVariant.values()).filter((entry) => String(entry.productId) === productId);
      if (!selected.length) {
        summary.hidden = true;
        summary.textContent = '';
        return;
      }

      const parts = selected.map((entry) => {
        const label = entry.variantTitle || (entry.options || []).filter(Boolean).join(' / ') || item.dataset.productTitle || 'Item';
        return label + ' ×' + entry.quantity;
      });
      summary.hidden = false;
      summary.textContent = 'Selected: ' + parts.join(' · ');
    }

    cleanVariantTitle(title) {
      const value = String(title || '').trim();
      return value === 'Default Title' ? '' : value;
    }

    clearItemStatus(item) {
      const status = item.querySelector('[data-paw-upsell-status]');
      if (status) {
        status.textContent = '';
        status.removeAttribute('data-state');
      }
    }

    setItemStatus(item, state, text) {
      const status = item.querySelector('[data-paw-upsell-status]');
      if (!status) return;
      status.textContent = text || '';
      status.dataset.state = state;
    }

    async fetchCartState() {
      try {
        const response = await fetch(this.shopifyRoot() + 'cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;
        this.applyCartState(await response.json());
      } catch (error) {
        console.warn('[Pawtique Upsell] Unable to read cart state', error);
      }
    }

    applyCartState(cart) {
      this.currencyCode = cart.currency || this.currencyCode;
      this.cartQtyByVariant = new Map();
      (cart.items || []).forEach((item) => {
        const id = String(item.variant_id || item.id);
        this.cartQtyByVariant.set(id, Number(this.cartQtyByVariant.get(id) || 0) + Number(item.quantity || 0));
      });

      this.cartItemsSubtotalCents = Number(cart.items_subtotal_price || 0);
      if (!this.cartItemsSubtotalCents) {
        this.cartItemsSubtotalCents = (cart.items || []).reduce((sum, item) => sum + Number(item.final_line_price || item.line_price || 0), 0);
      }
      this.cartTotalCents = Number(cart.total_price || this.cartItemsSubtotalCents || 0);
      this.cartItemCount = Number(cart.item_count || 0);
      this.updateCurrentFromCart(cart);
      this.updateTierThresholds();
      this.deriveOtherCartFactor();
      this.updateCartBubble(this.cartItemCount);
      this.syncAll();
    }

    updateTierThresholds() {
      const rate = this.getCurrencyRate();
      this.tiers.forEach((tier) => {
        tier.thresholdCents = Math.round(Number(tier.threshold || 0) * 100 * rate);
      });
    }

    getCurrencyRate() {
      if (this.currencyCode === this.shopCurrency) return 1;
      const globalRate = window.Shopify && window.Shopify.currency ? Number(window.Shopify.currency.rate || 0) : 0;
      if (globalRate > 0) return globalRate;
      return 1;
    }

    deriveOtherCartFactor() {
      if (!this.cartItemsSubtotalCents || !this.cartTotalCents) {
        this.otherCartFactor = 1;
        return;
      }
      const currentTier = this.getUnlockedTier(this.cartItemsSubtotalCents);
      const tierDiscount = currentTier && currentTier.kind === 'order' ? Number(currentTier.discount || 0) : 0;
      this.currentCartTierDiscount = tierDiscount;
      const expected = Math.max(1, Math.round(this.cartItemsSubtotalCents * (100 - tierDiscount) / 100));
      const factor = this.cartTotalCents / expected;
      this.otherCartFactor = Number.isFinite(factor) ? Math.max(0.2, Math.min(1.05, factor)) : 1;
    }

    getPendingSubtotal() {
      let total = 0;
      this.pendingByVariant.forEach((entry) => {
        total += Number(entry.priceCents || 0) * Number(entry.quantity || 0);
      });
      return total;
    }

    getUnlockedTier(amountCents) {
      let current = null;
      this.tiers.forEach((tier) => {
        if (amountCents >= tier.thresholdCents) current = tier;
      });
      return current;
    }

    getProjectedState() {
      const eligibleSubtotal = Math.max(0, this.cartItemsSubtotalCents + this.getPendingSubtotal());
      const unlocked = this.getUnlockedTier(eligibleSubtotal);
      const discount = unlocked && unlocked.kind === 'order' ? Number(unlocked.discount || 0) : 0;
      const afterTier = Math.round(eligibleSubtotal * (100 - discount) / 100);
      const projectedFinal = Math.max(0, Math.round(afterTier * this.otherCartFactor));
      const unlockedIndex = unlocked ? this.tiers.indexOf(unlocked) : -1;
      const next = this.tiers[unlockedIndex + 1] || null;
      return { eligibleSubtotal, unlocked, discount, projectedFinal, unlockedIndex, next };
    }

    updateProjection() {
      this.updateSelectedCount();
      if (!this.tierStatusEnabled || !this.tiers.length || !this.tierStatusEl) return;
      this.updateTierStatus(this.getProjectedState());
    }

    updateSelectedCount() {
      if (!this.selectedCountEl) return;
      if (!this.showSelectedCount) {
        this.selectedCountEl.hidden = true;
        this.selectedCountEl.textContent = '';
        return;
      }
      const count = Array.from(this.pendingByVariant.values()).reduce((sum, entry) => sum + Number(entry.quantity || 0), 0);
      if (count > 0) {
        this.selectedCountEl.hidden = false;
        this.selectedCountEl.textContent = count + (count === 1 ? ' item' : ' items');
      } else {
        this.selectedCountEl.hidden = true;
        this.selectedCountEl.textContent = '';
      }
    }

    updateTierStatus(state) {
      const current = state.unlocked;
      const next = state.next || (!current ? this.tiers[0] : null);

      if (!current && next) {
        const gap = Math.max(0, next.thresholdCents - state.projectedFinal);
        this.tierStatusEl.textContent = 'Add ' + this.formatMoney(gap) + ' to unlock ' + next.title + '!';
        return;
      }

      if (current && next) {
        let gap = Math.max(0, next.thresholdCents - state.projectedFinal);
        if (gap <= 0 && state.eligibleSubtotal < next.thresholdCents) gap = next.thresholdCents - state.eligibleSubtotal;
        this.tierStatusEl.textContent = current.title + ' unlocked · Add ' + this.formatMoney(gap) + ' to unlock ' + next.title;
        return;
      }

      this.tierStatusEl.textContent = current ? current.title + ' unlocked · Best offer reached' : '';
    }

    updateCartBubble(count) {
      document.querySelectorAll('.cart-link__bubble-num').forEach((el) => { el.textContent = String(count); });
      document.querySelectorAll('.cart-link__bubble').forEach((bubble) => {
        bubble.classList.toggle('cart-link__bubble--visible', count > 0);
      });
    }

    async commitPendingUpsells() {
      const items = Array.from(this.pendingByVariant.values()).map((pending) => ({
        id: Number(pending.variantId),
        quantity: Number(pending.quantity || 1),
        properties: { _pawtique_upsell: 'true' }
      }));

      if (!items.length) return true;

      try {
        const response = await fetch(this.shopifyRoot() + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });

        const raw = await response.text();
        let data;
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch (error) {
          throw new Error('The store returned a verification page. Please wait a moment and try again.');
        }

        if (!response.ok || (data && data.status)) {
          throw new Error((data && (data.description || data.message)) || 'Unable to add selected items.');
        }

        this.pendingByVariant.clear();
        await this.fetchCartState();
        return true;
      } catch (error) {
        console.warn('[Pawtique Upsell]', error);
        if (this.globalError) this.globalError.textContent = error && error.message ? error.message : 'Unable to add selected items.';
        return false;
      }
    }

    suppressCartDrawers() {
      [0, 50, 140, 280, 500].forEach((delay) => {
        window.setTimeout(() => {
          if (!(this.isOpen || document.documentElement.classList.contains('paw-upsell-awaiting'))) return;
          document.dispatchEvent(new CustomEvent('cart:close'));
          if (window.kachingCartApi && typeof window.kachingCartApi.closeCart === 'function') {
            try { window.kachingCartApi.closeCart(); } catch (error) {}
          }
        }, delay);
      });
    }

    open() {
      if (this.isOpen) return;
      this.finishInProgress = false;
      if (this.continueButton) this.continueButton.disabled = false;

      this.previousFocus = document.activeElement;
      this.root.hidden = false;
      document.documentElement.classList.remove('paw-upsell-awaiting');
      document.documentElement.classList.add('paw-upsell-open');
      this.isOpen = true;
      if (this.globalError) this.globalError.textContent = '';
      this.startCountdown();
      this.suppressCartDrawers();
      this.updateProjection();

      requestAnimationFrame(() => {
        const close = this.root.querySelector('.paw-upsell__close');
        if (close) close.focus({ preventScroll: true });
        else if (this.dialog) this.dialog.focus({ preventScroll: true });
      });
    }

    dismiss() {
      if (!this.isOpen || this.finishInProgress) return;
      this.pendingByVariant.clear();
      this.items.forEach((item) => {
        item._pawDraftQty = 1;
        this.clearItemStatus(item);
      });
      this.syncAll();
      this.closeModal(true);
    }

    closeModal(restoreFocus) {
      if (!this.isOpen) return;
      this.root.hidden = true;
      document.documentElement.classList.remove('paw-upsell-open', 'paw-upsell-awaiting');
      this.isOpen = false;
      if (restoreFocus && this.previousFocus && typeof this.previousFocus.focus === 'function') {
        this.previousFocus.focus({ preventScroll: true });
      }
    }

    async finish() {
      if (!this.isOpen || this.finishInProgress) return;
      this.finishInProgress = true;
      if (this.continueButton) this.continueButton.disabled = true;
      this.syncAll();

      const committed = await this.commitPendingUpsells();
      if (!committed) {
        this.finishInProgress = false;
        if (this.continueButton) this.continueButton.disabled = false;
        this.syncAll();
        return;
      }

      document.documentElement.classList.add('paw-upsell-finishing');
      document.dispatchEvent(new CustomEvent('cart:close'));
      this.closeModal(false);

      try {
        if (this.continueBehavior === 'checkout') {
          window.location.href = this.shopifyRoot() + 'checkout';
          return;
        }
        await this.openCartDrawer();
      } finally {
        document.documentElement.classList.remove('paw-upsell-finishing');
        this.finishInProgress = false;
        if (this.continueButton) this.continueButton.disabled = false;
      }
    }

    async openCartDrawer() {
      document.dispatchEvent(new CustomEvent('cart:build'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      if (window.kachingCartApi && typeof window.kachingCartApi.openCart === 'function') {
        await new Promise((resolve) => window.setTimeout(resolve, 180));
        window.kachingCartApi.openCart();
        return;
      }

      if (document.querySelector('#CartDrawer') && this.root.dataset.cartType !== 'page') {
        await new Promise((resolve) => window.setTimeout(resolve, 140));
        document.dispatchEvent(new CustomEvent('cart:open'));
        return;
      }

      window.location.href = this.root.dataset.cartUrl || '/cart';
    }

    startCountdown() {
      if (!this.timerMinutesEl || !this.timerSecondsEl || this.root.dataset.countdownEnabled !== 'true') return;
      if (this.timerId) {
        window.clearTimeout(this.timerId);
        this.timerId = null;
      }

      const key = 'pawtiqueUpsellOfferEndsAt:' + (this.root.dataset.productId || 'default');
      let endAt = 0;
      try { endAt = Number(window.sessionStorage.getItem(key) || 0); } catch (error) {}

      if (!endAt || endAt <= Date.now()) {
        endAt = Date.now() + this.countdownMinutes * 60 * 1000;
        try { window.sessionStorage.setItem(key, String(endAt)); } catch (error) {}
      }

      let lastRenderedSecond = -1;

      const render = () => {
        const now = Date.now();
        const remaining = Math.max(0, endAt - now);
        const totalSeconds = Math.max(0, Math.ceil(remaining / 1000));

        if (totalSeconds !== lastRenderedSecond) {
          const shouldPulse = lastRenderedSecond !== -1;
          lastRenderedSecond = totalSeconds;
          const minutes = Math.floor(totalSeconds / 60);
          const seconds = totalSeconds % 60;
          this.timerMinutesEl.textContent = String(minutes).padStart(2, '0');
          this.timerSecondsEl.textContent = String(seconds).padStart(2, '0');
          if (shouldPulse) this.pulseCountdownSecond();
        }

        if (totalSeconds <= 0) {
          this.timerId = null;
          return;
        }

        const msUntilNextSecond = remaining - (totalSeconds - 1) * 1000;
        const delay = Math.max(80, Math.min(1020, msUntilNextSecond + 24));
        this.timerId = window.setTimeout(render, delay);
      };

      render();
    }

    pulseCountdownSecond() {
      if (!this.timerSecondsEl) return;

      if (this.secondsPulseAnimation && typeof this.secondsPulseAnimation.cancel === 'function') {
        try { this.secondsPulseAnimation.cancel(); } catch (error) {}
      }

      if (typeof this.timerSecondsEl.animate === 'function') {
        this.secondsPulseAnimation = this.timerSecondsEl.animate(
          [
            { transform: 'scale(0.72)', opacity: 0.62, offset: 0 },
            { transform: 'scale(1.48)', opacity: 1, offset: 0.38 },
            { transform: 'scale(0.92)', opacity: 1, offset: 0.72 },
            { transform: 'scale(1.08)', opacity: 1, offset: 0.88 },
            { transform: 'scale(1)', opacity: 1, offset: 1 }
          ],
          {
            duration: 440,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            fill: 'none'
          }
        );
        return;
      }

      this.timerSecondsEl.classList.remove('paw-upsell__seconds-pulse');
      void this.timerSecondsEl.offsetWidth;
      this.timerSecondsEl.classList.add('paw-upsell__seconds-pulse');
      window.setTimeout(() => {
        if (this.timerSecondsEl) this.timerSecondsEl.classList.remove('paw-upsell__seconds-pulse');
      }, 380);
    }

    trapFocus(event) {
      const focusable = Array.from(this.dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'))
        .filter((el) => !el.hidden && el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    shopifyRoot() {
      return window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    }

    setMoney(element, cents) {
      if (element) element.textContent = this.formatMoney(cents);
    }

    formatMoney(cents) {
      const value = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: this.currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      } catch (error) {
        return value.toFixed(2) + ' ' + this.currencyCode;
      }
    }

    setImageSource(image, src) {
      if (!image || !src) return;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = src;
    }
  }

  const init = () => {
    const root = document.querySelector('[data-paw-upsell-root]');
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    new PawtiqueUpsellPopup(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
